import { US_STATE_NAMES } from "constants/constants";

export type ClaimableJurisdiction = {
	id: string;
	name: string;
	city?: string | null;
	county?: string | null;
	state?: string | null;
};

/**
 * Returns the county with the word "County", for example "Travis County".
 * Some rows store "Travis County" and others only "Travis".
 */
const formatCountyName = (county: string) => (/\bcounty$/i.test(county) ? county : `${county} County`);

/**
 * Returns the place line shown under a jurisdiction, for example "Austin, Texas" or
 * "Hays County, Texas". A jurisdiction without a city uses its county.
 */
export const formatJurisdictionRegion = (jurisdiction: ClaimableJurisdiction): string => {
	const stateName = jurisdiction.state ? US_STATE_NAMES[jurisdiction.state] || jurisdiction.state : "";

	let place = "";
	if (jurisdiction.city) {
		place = jurisdiction.city;
	} else if (jurisdiction.county) {
		place = formatCountyName(jurisdiction.county);
	}

	return [place, stateName].filter(Boolean).join(", ");
};

/**
 * Returns the jurisdictions whose name or place contain every word of the search, ignoring case.
 * For example "round tx" matches "Round Rock Fire Department" in "Round Rock, Texas".
 */
export const filterJurisdictionsBySearch = (
	jurisdictions: ClaimableJurisdiction[],
	searchText: string
): ClaimableJurisdiction[] => {
	const searchWords = searchText.toLowerCase().split(/\s+/).filter(Boolean);
	if (searchWords.length === 0) return jurisdictions;

	return jurisdictions.filter(jurisdiction => {
		const searchableText = [
			jurisdiction.name,
			jurisdiction.city,
			jurisdiction.county,
			jurisdiction.state,
			formatJurisdictionRegion(jurisdiction)
		]
			.filter(Boolean)
			.join(" ")
			.toLowerCase();

		return searchWords.every(word => searchableText.includes(word));
	});
};
